import { useMemo, useState } from 'react';

import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { DataTableColumnsSelector } from '@/components/data-table/data-table-columns-selector';
import {
  parseDateRange,
  serializeDateRange,
} from '@/components/data-table/filters-v1/components/data-table-date-range-picker-v2-filter';
import { DataTableFiltersV2, useTableFilters } from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useDataTable } from '@/hooks/use-data-table';
import { useModal } from '@/hooks/use-modal';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { TableIds } from '@/lib/constants/table-ids';
import { PageTemplate, usePermissions } from '@/modules/common';
import {
  DateRangePickerV2,
  DateRangePreset,
  DateRangeV2,
  detectPresetFromDates,
  getDateRangeFromPreset,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { RefreshData } from '@/modules/common/components/organisms/refresh-data';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { hashItemsForStaleness, useFinanceListRefresh } from '@/modules/finance/common';
import { QuickBooksNotConnectedMessage } from '@/modules/finance/common/components/quickbooks-not-connected-message';
import { useQuickBooksConnection } from '@/modules/finance/common/hooks/use-quickbooks-connection';
import { FcmNotificationType } from '@/modules/notifications';
import { AuthenticatedRoutes } from '@/router/routes';

import { getInvoices } from '../api';
import {
  DeleteInvoiceModal,
  InvoiceErrorModal,
  InvoiceInProgressModal,
  InvoicesTable,
} from '../components';
import {
  getInvoicesFilterConfig,
  getInvoicesTableColumns,
  SEARCH_FILTER_MIN_CHARACTERS,
} from '../constants';
import {
  INVOICES_FILTER_KEY_BILLING_PERIOD,
  INVOICES_TABLE_FILTER_KEYS,
  INVOICES_TABLE_FILTERS_STORAGE_KEY,
} from '../constants/invoices-filter-keys';
import { INVOICES_QUERY_KEY, mapInvoicesFilters, useInvoices, useRetryInvoice } from '../hooks';
import { InvoiceListItem, InvoiceStatus, InvoiceSyncOperation } from '../types';

/**
 * Structural hash for staleness detection on the list view. Status flipping
 * (e.g. in-progress → open) and the assigned invoice number are the only
 * server changes a list refresh needs to surface.
 */
const hashInvoiceListItems = (items: InvoiceListItem[] | undefined): string =>
  hashItemsForStaleness(items, item => ({ status: item.status, number: item.number }));

export const InvoicesPage = () => {
  const { t } = useTranslation(['authenticated.invoices', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { isQuickBooksConnected, isLoading: isQuickBooksLoading } = useQuickBooksConnection();

  const { hasPermission } = usePermissions();
  const isGenerateInvoicesEnabled = hasPermission(OpsiumPolicy.InvoicesGenerate);
  const isDeleteInvoicesEnabled = hasPermission(OpsiumPolicy.InvoicesDelete);

  const [deleteInvoice, setDeleteInvoice] = useState<InvoiceListItem | null>(null);
  const [errorInvoice, setErrorInvoice] = useState<InvoiceListItem | null>(null);
  const [inProgressInvoice, setInProgressInvoice] = useState<InvoiceListItem | null>(null);
  const {
    open: isErrorInfoModalOpen,
    onOpen: openErroInfoModal,
    onClose: closeErrorInfoModal,
  } = useModal();
  const {
    open: isInProgressInfoModalOpen,
    onOpen: openInProgressInfoModal,
    onClose: closeInProgressInfoModal,
  } = useModal();

  const closeDeleteInvoiceModal = () => setDeleteInvoice(null);
  const closeErrorModal = () => {
    closeErrorInfoModal();
    setErrorInvoice(null);
  };

  // Retry mutation
  const retryMutation = useRetryInvoice({
    onSuccess: () => {
      setErrorInvoice(null);
      closeErrorInfoModal();
    },
  });

  const columns = useMemo(
    () =>
      getInvoicesTableColumns({
        onDeleteInvoice: isDeleteInvoicesEnabled ? setDeleteInvoice : undefined,
        onErrorInfo: () => {
          openErroInfoModal();
        },
        onErrorInvoiceSelect: setErrorInvoice,
        onInProgressInfo: invoice => {
          setInProgressInvoice(invoice);
          openInProgressInfoModal();
        },
        onRetry: (invoiceId: number) => {
          retryMutation.mutate(invoiceId);
        },
        isRetrying: (invoiceId: number) => {
          return retryMutation.isPending && errorInvoice?.id === invoiceId;
        },
      }),
    [
      setDeleteInvoice,
      openErroInfoModal,
      openInProgressInfoModal,
      isDeleteInvoicesEnabled,
      retryMutation,
      errorInvoice,
    ]
  );

  const defaultBillingPeriod = useMemo(
    () => serializeDateRange(getDateRangeFromPreset(DateRangePreset.LastMonth)),
    []
  );

  usePersistedTableFilters({
    storageKey: INVOICES_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: INVOICES_TABLE_FILTER_KEYS,
    defaultFilters: { [INVOICES_FILTER_KEY_BILLING_PERIOD]: defaultBillingPeriod },
  });

  const [searchParams] = useSearchParams();
  const filterConfig = useMemo(() => getInvoicesFilterConfig(), []);
  const filterState = useTableFilters(filterConfig);
  const appliedFilters = filterState.activeFilters;

  // Billing period is a standalone date range picker (not a dropdown filter),
  // but its value is persisted in the same URL key via the shared filter state.
  // Read directly from searchParams so preset detection reacts to URL changes.
  // When the param is absent the range is null (reset state) — the backend then
  // returns all invoices regardless of date. The LastMonth default is seeded into
  // the URL on first visit by usePersistedTableFilters, not here.
  const { dates, selectedPreset } = useMemo(() => {
    const paramValue = searchParams.get(INVOICES_FILTER_KEY_BILLING_PERIOD);
    const parsed = paramValue ? parseDateRange(paramValue) : null;
    return { dates: parsed, selectedPreset: parsed ? detectPresetFromDates(parsed) : null };
  }, [searchParams]);

  const handleBillingPeriodChange = (newDates: DateRangeV2) => {
    filterState.setFilter(INVOICES_FILTER_KEY_BILLING_PERIOD, serializeDateRange(newDates));
  };

  const handleBillingPeriodPreset = (preset: DateRangePreset) => {
    handleBillingPeriodChange(getDateRangeFromPreset(preset));
  };

  // Reset clears the date filter entirely (loads all invoices) rather than
  // defaulting to today — see DateRangePickerV2.handleReset.
  const handleBillingPeriodReset = () => {
    filterState.clearFilter(INVOICES_FILTER_KEY_BILLING_PERIOD);
  };

  const filtersWithBillingPeriod = useMemo(() => {
    const billingPeriod = searchParams.get(INVOICES_FILTER_KEY_BILLING_PERIOD);
    return billingPeriod
      ? { ...appliedFilters, [INVOICES_FILTER_KEY_BILLING_PERIOD]: billingPeriod }
      : appliedFilters;
  }, [searchParams, appliedFilters]);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInvoices({
      filters: filtersWithBillingPeriod,
    });

  // Unified list refresh: FCM push when enabled, long-polling fallback otherwise.
  // FCM is disabled by default while backend delivery is unreliable
  // (OPS-1560 / OPS-1449) — see useFinanceListRefresh / useFcmForFinanceLists.
  const hasInProgressInvoice = (data?.items ?? []).some(
    invoice => invoice.status === InvoiceStatus.InProgress
  );
  const { hasPendingRefresh, refresh } = useFinanceListRefresh<InvoiceListItem>({
    notificationType: FcmNotificationType.INVOICE_READY,
    queryKey: [INVOICES_QUERY_KEY],
    filters: filtersWithBillingPeriod,
    items: data?.items,
    isFetching,
    hasInProgressItem: hasInProgressInvoice,
    fetchAll: pageSize =>
      getInvoices({ filters: mapInvoicesFilters(filtersWithBillingPeriod), pageSize }),
    hashItems: hashInvoiceListItems,
  });

  const hasNonBillingPeriodFiltersApplied = Object.entries(appliedFilters).some(
    ([filterKey, filterValue]) =>
      filterKey !== INVOICES_FILTER_KEY_BILLING_PERIOD && Boolean(filterValue)
  );
  const hasAppliedSearch = Boolean(
    appliedFilters.searchString &&
      typeof appliedFilters.searchString === 'string' &&
      appliedFilters.searchString.length >= SEARCH_FILTER_MIN_CHARACTERS
  );

  const invoicesCount = data?.pagination.total ?? 0;

  const { table, saveColumnSizing } = useDataTable<InvoiceListItem>({
    data: data?.items ?? [],
    columns,
    getRowId: row => row.id.toString(),
    tableId: TableIds.INVOICES,
  });

  const handleRowClick = (invoice: InvoiceListItem) => {
    if (invoice.status === InvoiceStatus.InProgress) return;

    navigate(AuthenticatedRoutes.InvoiceDetails.replace(':invoiceId', invoice.id.toString()), {
      state: { invoicesSearch: location.search },
    });
  };

  return (
    <PageTemplate
      title={t('title')}
      useStickyHeader
      className="p-0"
      headerClassName="items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]"
      contentClassName="relative"
      rightActions={
        isGenerateInvoicesEnabled && (
          <Button
            size="sm"
            onClick={() => navigate(AuthenticatedRoutes.GenerateInvoices)}
            disabled={!isQuickBooksConnected}
          >
            <CirclePlus />
            {t('generateInvoices')}
          </Button>
        )
      }
    >
      <Separator className="ml-[-16px] w-[calc(100%+32px)]" />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-4">
        {!isQuickBooksLoading && !isQuickBooksConnected && <QuickBooksNotConnectedMessage />}
        {hasPendingRefresh && (
          <RefreshData
            onRefresh={refresh}
            isPending={isLoading}
            dataUpdatedAt={null}
            description={t('refreshBanner.description')}
            buttonTitle={t('refreshBanner.buttonTitle')}
            containerClassName="mb-4"
          />
        )}
        <div className="flex items-start justify-between gap-2 pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePickerV2
              dates={dates}
              onChange={handleBillingPeriodChange}
              selectedPreset={selectedPreset}
              onPresetSelect={handleBillingPeriodPreset}
              onReset={handleBillingPeriodReset}
              defaultPrefix={t('authenticated.invoices:filters.billingPeriod')}
            />
            <DataTableFiltersV2
              filtersConfig={filterConfig}
              filterState={filterState}
              tableId={TableIds.INVOICES}
              className="flex flex-wrap gap-2"
            />
          </div>
          <div className="mt-1">
            <DataTableColumnsSelector table={table} />
          </div>
        </div>
        {/* Total count */}
        <p className="my-4 shrink-0 text-xs text-muted-foreground">
          {t(`common.invoicesCounter.${hasAppliedSearch ? 'withSearch' : 'default'}`, {
            count: invoicesCount,
          })}
        </p>
        <InvoicesTable
          table={table}
          onRowClick={handleRowClick}
          isLoading={isLoading || isFetchingNextPage}
          hasMore={hasNextPage}
          totalCount={invoicesCount}
          hasNonBillingPeriodFiltersApplied={hasNonBillingPeriodFiltersApplied}
          onLoadMore={fetchNextPage}
          stickyHeader
          fixedHeight
          tableId={TableIds.INVOICES}
          onResizeEnd={saveColumnSizing}
        />
        <DeleteInvoiceModal invoice={deleteInvoice} onClose={closeDeleteInvoiceModal} />
        <InvoiceErrorModal
          isOpen={isErrorInfoModalOpen}
          onClose={closeErrorModal}
          invoice={errorInvoice}
          syncOperation={errorInvoice?.syncOperation ?? null}
          syncError={errorInvoice?.syncError ?? null}
          onRetry={invoice => {
            retryMutation.mutate(invoice.id);
            closeErrorInfoModal();
          }}
          isRetrying={retryMutation.isPending}
        />
        <InvoiceInProgressModal
          isOpen={isInProgressInfoModalOpen}
          isUpdating={inProgressInvoice?.syncOperation === InvoiceSyncOperation.Update}
          onClose={() => {
            closeInProgressInfoModal();
            setInProgressInvoice(null);
          }}
        />
      </div>
    </PageTemplate>
  );
};
